import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { todayISO, fmtNum } from "../lib/format";
import { Printer } from "lucide-react";
import PrintFrame from "../components/PrintFrame";
import FileUpload from "../components/FileUpload";
import AttachmentThumb from "../components/AttachmentThumb";
import { canOperationalApprove, hasRole, useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const isPosInt = (v) => /^\d+$/.test(String(v)) && Number(v) > 0;
const isNonNegInt = (v) => v === "" || v === null || /^\d+$/.test(String(v));
const num = (v) => (v === "" || v === null ? 0 : Number(v));

export default function Laundry() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canMissing = canOperationalApprove(user);
  const [tab, setTab] = useState("dispatch");
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [masterStock, setMasterStock] = useState([]);
  const [printing, setPrinting] = useState(null);

  // ---- Dispatch form ----
  const [vendorId, setVendorId] = useState("");
  const [dDate, setDDate] = useState(todayISO());
  const [vehicle, setVehicle] = useState("");
  const [reference, setReference] = useState("");
  const [remarks, setRemarks] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [dispRows, setDispRows] = useState([]);
  const [dispSearch, setDispSearch] = useState("");
  const [showZeroDisp, setShowZeroDisp] = useState(false);
  const [dispBusy, setDispBusy] = useState(false);

  // ---- Receive form ----
  const [rVendorId, setRVendorId] = useState("");
  const [rDispatchId, setRDispatchId] = useState("");
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [rDate, setRDate] = useState(todayISO());
  const [rTo, setRTo] = useState("RETURNED_AWAITING");
  const [rRemarks, setRRemarks] = useState("");
  const [rAttachmentUrl, setRAttachmentUrl] = useState(null);
  const [rRows, setRRows] = useState([]);
  const [rBusy, setRBusy] = useState(false);
  const [rClientId, setRClientId] = useState("");

  const loadAll = async () => {
    const [i, v, d, m] = await Promise.all([
      api.get("/items"),
      api.get("/vendors"),
      api.get("/laundry/dispatches"),
      api.get("/reports/master-stock"),
    ]);
    setItems(i.data);
    setVendors(v.data);
    setDispatches(d.data);
    setMasterStock(m.data.rows || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadAll();
  }, []);

  const itemName = (id) => items.find((x) => x.id === id)?.name || id;

  // Build the Dirty-Holding checklist once a vendor is chosen.
  useEffect(() => {
    if (!vendorId) {
      setDispRows([]);
      return;
    }
    setDispRows(
      masterStock.map((m) => ({
        item_id: m.item_id,
        name: m.name,
        available: Number(m.DIRTY_HOLDING || 0),
        checked: false,
        qty: "",
        remark: "",
      })),
    );
  }, [vendorId, masterStock]);

  const updDisp = (id, k, v) =>
    setDispRows((rows) =>
      rows.map((r) => (r.item_id === id ? { ...r, [k]: v } : r)),
    );
  const visibleDisp = useMemo(
    () =>
      dispRows.filter(
        (r) =>
          (showZeroDisp || r.available > 0) &&
          r.name.toLowerCase().includes(dispSearch.toLowerCase()),
      ),
    [dispRows, showZeroDisp, dispSearch],
  );
  const setAllDisp = (checked) => {
    const ids = new Set(
      visibleDisp.filter((r) => r.available > 0).map((r) => r.item_id),
    );
    setDispRows((rows) =>
      rows.map((r) => (ids.has(r.item_id) ? { ...r, checked } : r)),
    );
  };
  const dispRowValid = (r) =>
    r.checked && isPosInt(r.qty) && Number(r.qty) <= r.available;
  const dispCheckedInvalid = dispRows.some(
    (r) => r.checked && !dispRowValid(r),
  );
  const dispValidCount = dispRows.filter(dispRowValid).length;

  const submitDispatch = async () => {
    if (!vendorId) {
      toast.error("Select a vendor");
      return;
    }
    if (dispCheckedInvalid) {
      toast.error(
        "Fix dispatch quantities (whole number, not exceeding Dirty Holding)",
      );
      return;
    }
    const clean = dispRows.filter(dispRowValid);
    if (clean.length === 0) {
      toast.error("Tick at least one item with a valid quantity");
      return;
    }
    setDispBusy(true);
    try {
      const { data } = await api.post("/laundry/dispatches", {
        vendor_id: vendorId,
        dispatch_date: dDate,
        vehicle,
        reference,
        remarks,
        attachment_url: attachmentUrl,
        lines: clean.map((r) => ({
          item_id: r.item_id,
          qty: Number(r.qty),
          remarks: r.remark || null,
        })),
      });
      toast.success(`Dispatch ${data.number} created`);
      setVehicle("");
      setReference("");
      setRemarks("");
      setAttachmentUrl(null);
      setVendorId("");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDispBusy(false);
    }
  };

  // ---- Receive ----
  const openDispatches = useMemo(
    () => dispatches.filter((d) => (d.outstanding_qty || 0) > 0),
    [dispatches],
  );
  const receivableForVendor = useMemo(
    () => openDispatches.filter((d) => !rVendorId || d.vendor_id === rVendorId),
    [openDispatches, rVendorId],
  );
  // Only active vendors can receive NEW dispatches; the Receive picker also keeps any inactive
  // vendor that still has outstanding (legacy) dispatches so their pending linen can be cleared.
  const activeVendors = useMemo(
    () => vendors.filter((v) => v.active !== false),
    [vendors],
  );
  const receiveVendors = useMemo(
    () =>
      vendors.filter(
        (v) =>
          v.active !== false ||
          openDispatches.some((d) => d.vendor_id === v.id),
      ),
    [vendors, openDispatches],
  );

  const loadDispatch = async (id) => {
    const { data } = await api.get(`/laundry/dispatches/${id}`);
    const outstanding = data.outstanding_by_item || {};
    const dl = data.dispatch.lines || [];
    setSelectedDispatch({ ...data.dispatch, id });
    setRRows(
      dl.map((l) => {
        const pending = Number(outstanding[l.item_id] || 0);
        return {
          item_id: l.item_id,
          name: itemName(l.item_id),
          dispatched: Number(l.qty || 0),
          pending,
          prevReturned: Number(l.qty || 0) - pending,
          checked: false,
          received: "",
          damaged: "",
          missing: "",
          remark: "",
        };
      }),
    );
    setRClientId(uid());
  };

  const pickReceiveVendor = (vId) => {
    setRVendorId(vId);
    setRDispatchId("");
    setSelectedDispatch(null);
    setRRows([]);
  };
  const pickReceiveDispatch = async (id) => {
    setRDispatchId(id);
    await loadDispatch(id);
  };

  const openReceiptFromHistory = async (d) => {
    setTab("receive");
    setRVendorId(d.vendor_id);
    setRDispatchId(d.id);
    await loadDispatch(d.id);
  };

  const updR = (id, k, v) =>
    setRRows((rows) =>
      rows.map((r) => (r.item_id === id ? { ...r, [k]: v } : r)),
    );
  const rRowValid = (r) => {
    if (![r.received, r.damaged, r.missing].every(isNonNegInt)) return false;
    const s = num(r.received) + num(r.damaged) + num(r.missing);
    return s > 0 && s <= r.pending;
  };
  const rCheckedInvalid = rRows.some((r) => r.checked && !rRowValid(r));
  const rValidCount = rRows.filter((r) => r.checked && rRowValid(r)).length;

  const submitReceipt = async () => {
    if (!selectedDispatch) {
      toast.error("Select a vendor and dispatch first");
      return;
    }
    if (rCheckedInvalid) {
      toast.error(
        "Received + Damaged + Missing must be whole numbers not exceeding Pending",
      );
      return;
    }
    const clean = rRows.filter((r) => r.checked && rRowValid(r));
    if (clean.length === 0) {
      toast.error("Tick at least one item and enter quantities");
      return;
    }
    setRBusy(true);
    try {
      const { data } = await api.post("/laundry/receipts", {
        dispatch_id: selectedDispatch.id,
        receipt_date: rDate,
        to_location: rTo,
        remarks: rRemarks,
        attachment_url: rAttachmentUrl,
        client_request_id: rClientId,
        lines: clean.map((r) => ({
          item_id: r.item_id,
          received: num(r.received),
          rejected: num(r.damaged),
          missing: num(r.missing),
        })),
      });
      toast.success(
        data?.duplicate ? "Receipt already recorded" : "Receipt recorded",
      );
      setRVendorId("");
      setRDispatchId("");
      setSelectedDispatch(null);
      setRRows([]);
      setRRemarks("");
      setRAttachmentUrl(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRBusy(false);
    }
  };

  return (
    <Layout>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="section-title" data-testid="page-title">
            {t("nav.laundry")}
          </h1>
          <div className="section-sub">{t("sub.laundry")}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="dispatch" data-testid="tab-dispatch">
            {t("lnd.tab.new")}
          </TabsTrigger>
          <TabsTrigger value="receive" data-testid="tab-receive">
            {t("lnd.tab.receive")}
          </TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">
            {t("lnd.tab.history")}
          </TabsTrigger>
        </TabsList>

        {/* -------------------- NEW DISPATCH -------------------- */}
        <TabsContent value="dispatch">
          <Card className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <Label>{t("col.vendor")}</Label>
                <Select value={vendorId} onValueChange={setVendorId}>
                  <SelectTrigger
                    className="h-12 mt-1"
                    data-testid="disp-vendor"
                  >
                    <SelectValue placeholder={t("col.vendor")} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeVendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("col.date")}</Label>
                <Input
                  type="date"
                  value={dDate}
                  onChange={(e) => setDDate(e.target.value)}
                  className="h-12 mt-1"
                  data-testid="disp-date"
                />
              </div>
              <div>
                <Label>{t("lnd.vehicle")}</Label>
                <Input
                  value={vehicle}
                  onChange={(e) => setVehicle(e.target.value)}
                  className="h-12 mt-1"
                  data-testid="disp-vehicle"
                />
              </div>
              <div>
                <Label>{t("col.ref")}</Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className="h-12 mt-1"
                  data-testid="disp-ref"
                />
              </div>
            </div>

            {!vendorId ? (
              <div
                className="p-3 rounded border border-slate-200 bg-slate-50 text-sm text-slate-600"
                data-testid="disp-pick-vendor"
              >
                Select a vendor to load linen currently in Dirty Holding.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-700">
                    Dirty Holding — select items to dispatch
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      placeholder="Search linen…"
                      value={dispSearch}
                      onChange={(e) => setDispSearch(e.target.value)}
                      className="h-9 w-44"
                      data-testid="disp-search"
                    />
                    {canMissing && (
                      <label className="flex items-center gap-2 text-xs text-slate-600 select-none">
                        <input
                          type="checkbox"
                          checked={showZeroDisp}
                          onChange={(e) => setShowZeroDisp(e.target.checked)}
                          data-testid="disp-show-zero"
                        />
                        Show zero-balance items
                      </label>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAllDisp(true)}
                      data-testid="disp-select-all"
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAllDisp(false)}
                      data-testid="disp-clear-all"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="disp-checklist">
                    <thead>
                      <tr>
                        <th className="w-8"></th>
                        <th>Linen Item</th>
                        <th className="num">Available in Dirty Holding</th>
                        <th className="num">Dispatch Quantity</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleDisp.map((r, i) => {
                        const disabled = r.available <= 0;
                        return (
                          <tr
                            key={r.item_id}
                            className={disabled ? "opacity-60" : ""}
                          >
                            <td>
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={r.checked}
                                onChange={(e) =>
                                  updDisp(
                                    r.item_id,
                                    "checked",
                                    e.target.checked,
                                  )
                                }
                                data-testid={`disp-check-${i}`}
                              />
                            </td>
                            <td>{r.name}</td>
                            <td className="num">{fmtNum(r.available)}</td>
                            <td className="num">
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                placeholder=""
                                value={r.qty}
                                disabled={!r.checked}
                                onChange={(e) =>
                                  updDisp(r.item_id, "qty", e.target.value)
                                }
                                className={`h-9 w-24 ml-auto text-right ${r.checked && !dispRowValid(r) ? "border-red-400" : ""}`}
                                data-testid={`disp-qty-${i}`}
                              />
                            </td>
                            <td>
                              <Input
                                value={r.remark}
                                disabled={!r.checked}
                                onChange={(e) =>
                                  updDisp(r.item_id, "remark", e.target.value)
                                }
                                className="h-9"
                                data-testid={`disp-remark-${i}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {visibleDisp.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-slate-500 py-4"
                          >
                            No linen in Dirty Holding
                            {dispSearch ? " for this search" : ""}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Only ticked rows are dispatched. Quantity cannot exceed the
                  Dirty Holding balance.
                </div>

                <div className="mt-4">
                  <Label>{t("col.remarks")}</Label>
                  <Textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    data-testid="disp-remarks"
                  />
                </div>
                <div className="mt-4">
                  <Label>{t("lnd.attach_challan")}</Label>
                  <div className="mt-1">
                    <FileUpload
                      value={attachmentUrl}
                      onChange={setAttachmentUrl}
                      testid="disp-attachment"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    className="h-12 bg-slate-900 hover:bg-slate-800 text-white px-8"
                    onClick={submitDispatch}
                    disabled={
                      dispBusy || dispValidCount === 0 || dispCheckedInvalid
                    }
                    data-testid="disp-submit"
                  >
                    {dispBusy ? "Creating…" : t("lnd.create_dispatch")}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        {/* -------------------- RECEIVE RETURN -------------------- */}
        <TabsContent value="receive">
          <Card className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label>{t("col.vendor")}</Label>
                <Select value={rVendorId} onValueChange={pickReceiveVendor}>
                  <SelectTrigger className="h-12 mt-1" data-testid="rcv-vendor">
                    <SelectValue placeholder={t("col.vendor")} />
                  </SelectTrigger>
                  <SelectContent>
                    {receiveVendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                        {v.active === false ? " (inactive)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Dispatch / Challan</Label>
                <Select
                  value={rDispatchId}
                  onValueChange={pickReceiveDispatch}
                  disabled={!rVendorId}
                >
                  <SelectTrigger
                    className="h-12 mt-1"
                    data-testid="rcv-dispatch"
                  >
                    <SelectValue
                      placeholder={
                        rVendorId
                          ? "Select outstanding dispatch"
                          : "Pick vendor first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {receivableForVendor.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.number} · {d.dispatch_date} · pending{" "}
                        {fmtNum(d.outstanding_qty)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("lnd.return_date")}</Label>
                <Input
                  type="date"
                  value={rDate}
                  onChange={(e) => setRDate(e.target.value)}
                  className="h-12 mt-1"
                  data-testid="rcv-date"
                />
              </div>
            </div>

            {!selectedDispatch ? (
              <div className="p-3 rounded border border-slate-200 bg-slate-50 text-sm text-slate-600">
                Select a vendor and an outstanding dispatch to load the linen
                pending against it.
              </div>
            ) : (
              <div>
                <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-lg font-serif">
                      Dispatch {selectedDispatch.number}
                    </div>
                    <div className="text-xs text-slate-500">
                      Vendor: {selectedDispatch.vendor_name} · Date:{" "}
                      {selectedDispatch.dispatch_date}
                    </div>
                  </div>
                  <div>
                    <Label>{t("lnd.move_to")}</Label>
                    <Select value={rTo} onValueChange={setRTo}>
                      <SelectTrigger
                        className="h-11 w-52 mt-1"
                        data-testid="rcv-to"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RETURNED_AWAITING">
                          {t("lnd.returned_await")}
                        </SelectItem>
                        <SelectItem value="STORE_CLEAN">
                          {t("lnd.direct_store")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setRRows((rows) =>
                        rows.map((r) =>
                          r.pending > 0 ? { ...r, checked: true } : r,
                        ),
                      )
                    }
                    data-testid="rcv-select-all"
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setRRows((rows) =>
                        rows.map((r) => ({ ...r, checked: false })),
                      )
                    }
                    data-testid="rcv-clear-all"
                  >
                    Clear All
                  </Button>
                </div>
                {!canMissing && (
                  <div className="mb-2 text-xs text-slate-500">
                    Missing quantities can only be recorded by a
                    Supervisor/Admin.
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="rcv-checklist">
                    <thead>
                      <tr>
                        <th className="w-8"></th>
                        <th>Linen Item</th>
                        <th className="num">Originally Dispatched</th>
                        <th className="num">Previously Returned</th>
                        <th className="num">Pending with Vendor</th>
                        <th className="num">Qty Received</th>
                        <th className="num">Damaged</th>
                        <th className="num">Missing</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rRows.map((r, i) => {
                        const disabled = r.pending <= 0;
                        return (
                          <tr
                            key={r.item_id}
                            className={disabled ? "opacity-60" : ""}
                          >
                            <td>
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={r.checked}
                                onChange={(e) =>
                                  updR(r.item_id, "checked", e.target.checked)
                                }
                                data-testid={`rcv-check-${i}`}
                              />
                            </td>
                            <td>{r.name}</td>
                            <td className="num">{fmtNum(r.dispatched)}</td>
                            <td className="num">{fmtNum(r.prevReturned)}</td>
                            <td className="num">{fmtNum(r.pending)}</td>
                            <td className="num">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder=""
                                value={r.received}
                                disabled={!r.checked}
                                onChange={(e) =>
                                  updR(r.item_id, "received", e.target.value)
                                }
                                className="h-9 w-20 ml-auto text-right"
                                data-testid={`rcv-received-${i}`}
                              />
                            </td>
                            <td className="num">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder=""
                                value={r.damaged}
                                disabled={!r.checked}
                                onChange={(e) =>
                                  updR(r.item_id, "damaged", e.target.value)
                                }
                                className="h-9 w-20 ml-auto text-right"
                                data-testid={`rcv-damaged-${i}`}
                              />
                            </td>
                            <td className="num">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder=""
                                value={r.missing}
                                disabled={!r.checked || !canMissing}
                                onChange={(e) =>
                                  updR(r.item_id, "missing", e.target.value)
                                }
                                className="h-9 w-20 ml-auto text-right"
                                data-testid={`rcv-missing-${i}`}
                              />
                            </td>
                            <td>
                              <Input
                                value={r.remark}
                                disabled={!r.checked}
                                onChange={(e) =>
                                  updR(r.item_id, "remark", e.target.value)
                                }
                                className="h-9"
                                data-testid={`rcv-remark-${i}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Received + Damaged + Missing cannot exceed Pending. Unresolved
                  quantities remain pending with the vendor.
                </div>

                <div className="mt-4">
                  <Label>Remarks</Label>
                  <Textarea
                    value={rRemarks}
                    onChange={(e) => setRRemarks(e.target.value)}
                    data-testid="rcv-remarks"
                  />
                </div>
                <div className="mt-4">
                  <Label>Attachment (delivery note / PDF)</Label>
                  <div className="mt-1">
                    <FileUpload
                      value={rAttachmentUrl}
                      onChange={setRAttachmentUrl}
                      testid="rcv-attachment"
                    />
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRDispatchId("");
                      setSelectedDispatch(null);
                      setRRows([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    onClick={submitReceipt}
                    disabled={rBusy || rValidCount === 0 || rCheckedInvalid}
                    data-testid="rcv-submit"
                  >
                    {rBusy ? "Submitting…" : "Submit Receipt"}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* -------------------- HISTORY (unchanged) -------------------- */}
        <TabsContent value="list">
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="vr-table" data-testid="dispatch-history">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Status</th>
                    <th className="num">Items</th>
                    <th className="num">Outstanding</th>
                    <th>Attachment</th>
                    <th className="no-print">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatches.map((d) => (
                    <tr key={d.id}>
                      <td className="font-mono text-xs">{d.number}</td>
                      <td>{d.dispatch_date}</td>
                      <td>{d.vendor_name}</td>
                      <td>
                        <Badge
                          className={
                            d.status === "closed"
                              ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                              : d.status === "partial"
                                ? "bg-amber-100 text-amber-800 border border-amber-200"
                                : "bg-slate-100 text-slate-700 border"
                          }
                        >
                          {d.status}
                        </Badge>
                      </td>
                      <td className="num">{d.lines?.length}</td>
                      <td className="num">{fmtNum(d.outstanding_qty)}</td>
                      <td>
                        <AttachmentThumb
                          url={d.attachment_url}
                          testid={`disp-thumb-${d.number}`}
                        />
                      </td>
                      <td className="no-print">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReceiptFromHistory(d)}
                          data-testid={`open-receipt-${d.number}`}
                          disabled={(d.outstanding_qty || 0) === 0}
                        >
                          Open
                        </Button>{" "}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPrinting(d)}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!printing} onOpenChange={() => setPrinting(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Dispatch Challan</DialogTitle>
          </DialogHeader>
          <PrintFrame
            title={`Dispatch Challan ${printing?.number || ""}`}
            subtitle={`Vendor: ${printing?.vendor_name} · Date: ${printing?.dispatch_date}`}
            user={user}
          >
            <table className="vr-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(printing?.lines || []).map((l, i) => (
                  <tr key={l.item_id || i}>
                    <td>{itemName(l.item_id)}</td>
                    <td className="num">{fmtNum(l.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {printing?.remarks && (
              <div className="mt-3 text-sm">Remarks: {printing.remarks}</div>
            )}
          </PrintFrame>
          <DialogFooter>
            <Button onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
